/**
 * Batch P2 — Manual super-admin credit top-up dialog.
 *
 * Calls ``POST /admin/tenants/{id}/credits/topup`` with a per-mount
 * ``idempotency_key`` so a double-click on Submit is a single-grant
 * operation (the backend short-circuits to the prior ledger row on
 * matching key + payload).
 *
 * TOTP re-prompt — interim friction (CTO ruling, Batch P2):
 * The dialog asks the SA to re-enter their 6-digit TOTP code as UI
 * friction before allowing submit. The code is NOT verified
 * server-side in this batch — the existing /admin/auth/login/mfa-verify
 * endpoint requires an mfa_pending token (issued only at the password
 * stage of admin login), not the access JWT this surface holds. A
 * proper require_recent_mfa dep that gates ALL SA mutations is
 * BACKLOG'd as P1-mfa-recency-dep — when it ships, this re-prompt
 * graduates from cosmetic friction to a server-enforced step-up.
 *
 * Until then the UI re-prompt buys two things:
 *   1. Slows down a stolen-cookie attacker by one TOTP rotation step
 *      (any human at the keyboard can type six digits; an automated
 *      replay bot needs the live OTP secret).
 *   2. Surfaces operator intent — the act of opening the authenticator
 *      app is a forcing function that "yes, I really mean to grant
 *      credits to this tenant."
 *
 * Sec Lead's full M1 surface (server-enforced 15-min recency) lands
 * with P1-mfa-recency-dep.
 */
import { useMemo, useState } from "react";
import {
  useTopUpCredits,
  type TenantDetail,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenant: TenantDetail;
  onClose: () => void;
}

const MAX_AMOUNT = 10_000;
const MIN_REASON = 3;
const MAX_REASON = 200;

function generateIdempotencyKey(): string {
  // crypto.randomUUID is widely supported in evergreen browsers and the
  // jsdom environment vitest >= 1.0 exposes via globalThis. The key is
  // prefixed ``ui_`` so backend forensics can distinguish UI-originated
  // grants from ones a future CLI might issue.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `ui_${crypto.randomUUID().replace(/-/g, "")}`;
  }
  return `ui_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function TopUpCreditsDialog({ tenant, onClose }: Props): JSX.Element {
  const [amount, setAmount] = useState<string>("");
  const [reason, setReason] = useState("");
  const [totp, setTotp] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const topUp = useTopUpCredits(tenant.id);
  const addToast = useToastStore((s) => s.addToast);

  // Stable per-mount key — closing + re-opening the modal mints a fresh
  // key (so two intentional grants are not collapsed by idempotency).
  const idempotencyKey = useMemo(generateIdempotencyKey, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);

    const n = Number.parseInt(amount, 10);
    if (!Number.isFinite(n) || n < 1 || n > MAX_AMOUNT) {
      setErrMsg(`Amount must be between 1 and ${MAX_AMOUNT.toLocaleString()}.`);
      return;
    }
    const trimmedReason = reason.trim();
    if (trimmedReason.length < MIN_REASON) {
      setErrMsg(`Reason is required (min ${MIN_REASON} characters).`);
      return;
    }
    if (trimmedReason.length > MAX_REASON) {
      setErrMsg(`Reason must be ${MAX_REASON} characters or fewer.`);
      return;
    }
    // TOTP re-prompt: format-only check until P1-mfa-recency-dep ships
    // (see file header). Six digits, all numeric.
    if (!/^\d{6}$/.test(totp)) {
      setErrMsg("Enter the 6-digit code from your authenticator app.");
      return;
    }

    topUp.mutate(
      {
        amount: n,
        reason: trimmedReason,
        idempotency_key: idempotencyKey,
      },
      {
        onSuccess: (data) => {
          addToast(
            `Granted ${n.toLocaleString()} credits to ${tenant.name}. ` +
              `Balance: ${data.balance_after.toLocaleString()}.`,
            "success",
          );
          onClose();
        },
        onError: (err) => {
          setErrMsg(err.message || "Could not top up credits.");
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
          Top up credits — {tenant.name}
        </h2>
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Grants the specified credits to this tenant. Current balance:{" "}
          <strong className="text-text-primary dark:text-text-primary">
            {tenant.current_credits.toLocaleString()}
          </strong>
          . Reason is recorded in the audit log and visible to the tenant.
        </p>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Amount (credits)
          </span>
          <input
            type="number"
            min={1}
            max={MAX_AMOUNT}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            placeholder="e.g. 100"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Reason (required, 3-200 chars)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={MIN_REASON}
            maxLength={MAX_REASON}
            rows={3}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            placeholder="e.g. Goodwill grant after support incident; ticket #42"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Authenticator code (6 digits)
          </span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
            required
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            placeholder="123456"
          />
          <span className="mt-1 block text-xs text-accent-primary dark:text-brand-100/50">
            Re-prompt friction; P1-mfa-recency-dep makes this server-enforced.
          </span>
        </label>
        {errMsg ? (
          <p
            role="alert"
            className="text-sm text-red-600 dark:text-red-400"
          >
            {errMsg}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={topUp.isPending}
            className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
          >
            {topUp.isPending ? "Granting…" : "Grant credits"}
          </button>
        </div>
      </form>
    </div>
  );
}
